import React, { useContext, useEffect, useState } from "react";
import {
  parseParameters,
  composeApplyCommand,
  configToForm,
  defaultPort,
  isTypeSubscriberGroupDataAllowed,
  parseBillingSections,
  composeClearCommand,
} from "./api";

import SwitchInput from "common/SwitchInputWithState";
import BillingProviderSettings, { billingProviders } from "./Providers";
import TextInput from "common/TextInput";
import NumberInput from "common/NumberInputWithSpinner";
import SelectInput from "common/SelectInput";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import help from "help/viewBillingConf.md";
import SettingsView, { DefaultContextMenu } from "common/SettingsView";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";
import Header from "common/Header";
import { BqnTabs } from "common/BqnTabs";
import Request from "common/Request";

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const dualStackSubscriberGroupOptions = {
  splynx: [
    { label: "No IPv6 dual-stack groups", value: "disabled" },
    { label: "Username", value: "username" },
    { label: "Login", value: "login" },
    { label: "Customer-Id", value: "customer-id" },
    { label: "Username + Customer-Id", value: "username + customer-id" },
    { label: "Login + Customer-Id", value: "login + customer-id" },
  ],
  sonar: [
    { label: "No IPv6 dual-stack groups", value: "disabled" },
    { label: "Name", value: "name" },
    { label: "Customer-Id", value: "customer-id" },
    { label: "Name + Customer-Id", value: "name + customer-id" },
  ],
  gaiia: [
    { label: "No IPv6 dual-stack groups", value: "disabled" },
    { label: "Customer-Id", value: "customer-id" },
    { label: "Name", value: "name" },
    { label: "Name + Customer-Id", value: "name + customer-id" },
  ],
  azotel: [
    { label: "No IPv6 dual-stack groups", value: "disabled" },
    { label: "Customer-Id", value: "customer-id" },
    { label: "Name", value: "name" },
    { label: "Name + Customer-Id", value: "name + customer-id" },
  ],
};

const splynxLocationGroupOptions = [
  { label: "Disabled", value: "disabled" },
  { label: "Network Sites", value: "network-sites" },
  { label: "Labels", value: "labels" },
  { label: "Network Sites + Labels", value: "network-sites-labels" },
];

const AutoCongestionManagementInput = ({
  onChange,
  defaults = {},
  className,
}) => (
  <>
    <SwitchInput
      defaultChecked={defaults.acmEnabled}
      className={`input-group margin-t-5 ${className ?? ""}`}
      name="acmEnabled"
      label="Automatic-Congestion-Management"
      onChange={onChange}
    />
  </>
);

const RateLimitInput = ({
  name = "rateLimitPercentage",
  onChange,
  defaults = {},
  className,
}) => (
  <>
    <NumberInput
      max={200}
      min={1}
      defaultValue={defaults[name] || 100}
      className={`input-group ${className ?? ""}`}
      name={name}
      label="Rate-Limit Scaling (%)"
      onChange={onChange}
    />
  </>
);

const subscriberGroupDataLabelsForProvider = {
  'azotel': "Create subs-groups with the CPE parent nickname",
  'gaiia': "Create subs-groups with the upstream device of inventory item",
  'powercode': "Create subs-groups with the Equipment LocationID",
  'sonar': "Create subs-groups with the parent inventory items",
  'uisp': "Create subs-groups with device and site location",
  'visp': "Create subs-groups with the CPE access point identifier",
  'wispro': "Create subs-groups with contract node name",
}

const getSGProviderLabel = provider =>
  subscriberGroupDataLabelsForProvider[provider] || 'Obtain subscriber-group information'

const SubscriberGroupDataInput = ({
  onChange,
  state = {},
  defaults = {},
  className,
}) =>
{
  return isTypeSubscriberGroupDataAllowed(state.type) ? (
    <SwitchInput
      defaultChecked={defaults.subscriberGroupData ?? (state.type === 'uisp' ? true : false)}
      className={`input-group margin-t-5 ${className ?? ""}`}
      name="subscriberGroupData"
      label={`${getSGProviderLabel(state.type)}`}
      onChange={onChange}
    />
  ) : (
    <div></div>
  );
}

const BlockSubscribersInput = ({ onChange, defaults = {}, className }) => (
  <>
    <SwitchInput
      defaultChecked={defaults.blocking}
      className={`input-group ${className ?? ""}`}
      name="blocking"
      label="Block Inactive/Not Paying Subscribers"
      onChange={onChange}
    />
  </>
);

const ServerAndPortSettingsInput = ({ onChange, defaults = {}, state }) => (
  <>
    <TextInput
      type="text"
      name="server"
      label="Server:"
      defaultValue={defaults.server}
      onChange={onChange}
      className="input-control"
    />
    <TextInput
      type="text"
      name="port"
      label="Port:"
      placeHolder={defaultPort(state.type)}
      className="input-control"
      defaultValue={defaults.port}
      onChange={onChange}
    />
  </>
);

const getSplynxLocationGroup=(value)=>{
  if(!value){
    return 'disabled'
  }
  if (value === "network-sites labels"){
    return 'network-sites-labels'
  }
  return value;
}

const EnabledBillingSettings = ({
  onChange,
  defaults = {},
  state = {},
  ...settings
}) => {
  return (
    <>
      {state.type !== undefined ? (
        <>
          <ServerAndPortSettingsInput
            onChange={onChange}
            defaults={defaults}
            state={state}
          />
        </>
      ) : null}
      <BillingProviderSettings
        onChange={onChange}
        defaults={defaults}
        state={state}
        {...settings}
      />
      <AutoCongestionManagementInput
        onChange={onChange}
        defaults={defaults}
        state={state}
        className="input-group align-top"
      />
      {["splynx", "gaiia"].includes(state.type) ? (
        <SelectInput
          label="IPv6 dual-stack group name"
          name="dualStackSubscriberGroup"
          className="input-group"
          onChange={onChange}
          defaultValue={defaults.dualStackSubscriberGroup}
          options={dualStackSubscriberGroupOptions[state.type]}
        />
      ): null}
      <SubscriberGroupDataInput
        onChange={onChange}
        defaults={defaults}
        state={state}
        className="input-group align-top"
      />
      {["sonar", "azotel"].includes(state.type) ? (
        <SelectInput
          label="IPv6 dual-stack group name"
          name="dualStackSubscriberGroup"
          className="margin-b-20"
          onChange={onChange}
          defaultValue={defaults.dualStackSubscriberGroup}
          options={dualStackSubscriberGroupOptions[state.type]}
        />
      ) : null}
      {state.type === "splynx" && (
        <SelectInput
          label="Create subs-groups"
          name="splynxLocationGroup"
          className="input-group"
          onChange={onChange}
          defaultValue={getSplynxLocationGroup(defaults.splynxLocationGroup)}
          options={splynxLocationGroupOptions}
        />
      )}
    </>
  );
};

const BillingSettings = ({
  onChange,
  defaults = {},
  state = {},
  ...settings
}) => (
  <ArrangeInColumns columns={2} rowGap={0}>
    <SwitchInput
      name="enabled"
      className="input-group billing-view"
      defaultChecked={defaults.enabled}
      onChange={onChange}
      label="Integration with billing system"
    />
    {state.enabled === true ? (
      <>
        <div />
        <SelectInput
          name="type"
          className="input-group"
          onChange={onChange}
          title="Billing"
          defaultValue={defaults.type || "azotel"}
          options={billingProviders}
          label="Billing type:"
        />
        <div />
        <RateLimitInput
          onChange={onChange}
          defaults={defaults}
          state={state}
          className="margin-b-20"
        />
        <BlockSubscribersInput
          onChange={onChange}
          defaults={defaults}
          state={state}
          className="input-group"
        />
        <EnabledBillingSettings
          onChange={onChange}
          defaults={defaults}
          state={state}
          {...settings}
        />
      </>
    ) : null}
  </ArrangeInColumns>
);

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const convertOnOffToBoolean = (value, defaultValue = undefined) =>
  value === undefined && defaultValue !== undefined
    ? defaultValue
    : typeof value === "boolean"
    ? value
    : value === "on";

const convertTrueFalseToBoolean = (value, defaultValue = undefined) =>
  value === undefined && defaultValue !== undefined
    ? defaultValue
    : typeof value === "boolean"
    ? value
    : value === "true";

const ConfigBilling = ({returnView}) => {
  const [request, setRequest] = useState(null);
  const [bqnDisplayed, setBqnDisplayed] = useState(null);

  const actions = useContext(ActionsContext);
  const { bqnId: bqnIdContext, clusterStatus, loginBqnId, bqns } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const bqnId = bqnIdContext === "all" ? loginBqnId : bqnIdContext;

  const loadApiBillingParameters = () =>
    ifCl
      .run(atBqnTarget("show api billing parameters", mode, bqnId))
      .then(parseParameters(mode, bqnId))
      .then(configToForm);

  const load = () => {
    actions.send("do-load-billing-status");
    const req = loadApiBillingParameters();
    setRequest(req);
    return req;
  };

  useEffect(() => {
    if (bqnDisplayed === null || bqnDisplayed === undefined) {
      return;
    }
    load();
  }, [bqnDisplayed]);

  useEffect(() => {
    setBqnDisplayed(bqnId);
  }, [bqnId]);

  const checkNothingPending = () =>
    ifCl.run("show config diff").then((response) => {
      if (response.length > 0) {
        throw new Error(CANT_PROCEED_ERROR);
      }
    });

  const submitCommand = (command) =>  ifCl.run(command, /*is batch*/ true);

  const clearConfigAttempt = () =>
    ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

  const notifyAndRevert = (error) => {
    error = error.message === undefined ? new Error(error) : error;
    clearConfigAttempt();
    throw error;
  };

  const normalizeForm = ({
    enabled,
    acmEnabled,
    blocking,
    sonarDelinquent,
    sonarPolicyName,
    vispBlockingBasedOnStatus,
    vispPolicyName,
    subscriberGroupData,
    powercodePolicyName,
    azotelPolicyName,
    ...settings
  }) => {
    const result = {
      enabled: convertOnOffToBoolean(enabled),
      acmEnabled: convertOnOffToBoolean(acmEnabled),
      blocking: convertOnOffToBoolean(blocking),
      subscriberGroupData: convertOnOffToBoolean(subscriberGroupData),
      sonarDelinquent: convertTrueFalseToBoolean(sonarDelinquent),
      sonarPolicyName: convertOnOffToBoolean(sonarPolicyName),
      powercodePolicyName: convertOnOffToBoolean(powercodePolicyName),
      vispPolicyName: convertOnOffToBoolean(vispPolicyName),
      vispBlockingBasedOnStatus: convertTrueFalseToBoolean(
        vispBlockingBasedOnStatus
      ),
      azotelPolicyName: convertOnOffToBoolean(azotelPolicyName),
      ...settings,
    };
    return result;
  };

  const cleanConfig = (originalSettings, update, bqn) => {
    const requiresClear =
      originalSettings.enabled === true &&
      (update.enabled === false || originalSettings.type !== update.type);

    if (requiresClear !== true) {
      return Promise.resolve("");
    }
    if (mode === "standalone") {
      return Promise.resolve("no api billing");
    }
    return ifCl
      .run("show config running")
      .then((config) =>
        composeClearCommand(parseBillingSections(config), bqns, bqn)
      );
  };

  const apply = (settings, originalSettings = {}, bqn) => {
    const update = normalizeForm(settings);

    return cleanConfig(originalSettings, update, bqn)
      .then((clears) => {
        const buildCommand = () =>
          composeApplyCommand(
            originalSettings,
            update,
            mode,
            bqn,
            clears,
          );

        return checkNothingPending().then(buildCommand).then(submitCommand);
      })
      .catch(notifyAndRevert);
  };

  const getData = (initial, data) => {
    if (initial) {
      return Promise.resolve(data);
    } else {
      return load();
    }
  };

  return bqnId === undefined ? (
    <Loading />
  ) : (
    <>
      <div className="container-lg">
        <div className="row clearfix">
          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div className="card">
              <Header
                title="Billing Connector Configuration"
                subtitle={
                  <small>
                    Press <code>Apply</code> button at the end of the page to
                    set the new configuration values.
                  </small>
                }
                backButton={!!returnView}
                returnUrl={returnView}
                contextMenu={
                  <DefaultContextMenu
                    doLoad={() => {
                      load();
                    }}
                    Help={true}
                  />
                }
                bqnSelector={true}
              />
            </div>
          </div>
        </div>
      </div>
      <BqnTabs value={bqnDisplayed} showAll={false} />
      <Request during={request}>
        {(result) => {
          if (bqnDisplayed === null) {
            return null;
          }

          return (
            <SettingsView
              key={`billing-config-bqn${bqnDisplayed}`}
              title="Billing Connector Configuration"
              load={(initial) => getData(initial, result)}
              apply={(settings, originalSettings) =>
                apply(settings, originalSettings, bqnDisplayed)
              }
              clear={clear}
              mdHelp={help}
              applyActionClassName="hidden-to-operators"
              bqnSelector={false}
              noHeader={true}
              returnView={returnView}
            >
              <BillingSettings />
            </SettingsView>
          );
        }}
      </Request>
    </>
  );
};

export default ConfigBilling;
